import { memo, useMemo } from 'react';

import {
  useGetNewTrendingGamesQuery,
  useGetRegionsQuery,
} from 'store/api/configService/analyticsController';

import { appendTopGameColors } from 'utils/analytics';
import { findPathPages } from 'utils/pages';

import ArrowCircleLink from 'components/UI/ArrowCircleLink/ArrowCircleLink';
import Loader from 'components/UI/Loader/Loader';
import NoData from 'components/UI/NoData/NoData';
import Tooltip from 'components/UI/Tooltip/Tooltip';
import Typography from 'components/UI/Typography/Typography';
import GameByGeo from 'components/pages/Analytics/GamesByGeo/GameByGeo/GameByGeo';
import Section from 'components/pages/Analytics/Layout/Section/Section';

import { TOP_GAME_COLORS } from 'components/config/analytics';
import { PAGE_PATH } from 'components/config/pages';

import { ReactComponent as InfoIcon } from 'images/Icons/Info.svg';

import styles from './GamesByGeo.module.scss';

const GamesByGeo = () => {
  const { data, isLoading: dataLoading } = useGetNewTrendingGamesQuery();
  const { data: regions, isLoading: regionsLoading } = useGetRegionsQuery();

  const games = useMemo(
    () => appendTopGameColors(data, TOP_GAME_COLORS.byGeo),
    [data],
  );

  const gamesJSX = useMemo(
    () =>
      games?.map(
        ({ game: { gameName, gameCode, icon }, region, backgroundColor }) => {
          const currentRegion = regions?.find(
            (item) => item.region.code === region,
          );

          return (
            <GameByGeo
              key={gameCode}
              name={gameName}
              gameCode={gameCode}
              icon={icon}
              geo={currentRegion?.region.name}
              backgroundColor={backgroundColor}
              backgroundImage={`url(${currentRegion?.image})`}
            />
          );
        },
      ),
    [games, regions],
  );

  const isLoading = useMemo(
    () => [dataLoading, regionsLoading].some(Boolean),
    [dataLoading, regionsLoading],
  );

  return (
    <Section className={styles.section}>
      <div className={styles.header}>
        <div className={styles.titleWrapper}>
          <Typography type="heading-md" weight="medium" uppercase>
            New Trending games by geo
          </Typography>
          <Tooltip title="The number of potential players in different countries varies significantly, which can impact game statistics and performance indicators.">
            <InfoIcon />
          </Tooltip>
        </div>
        <ArrowCircleLink
          title="All Evoplay Games"
          path={findPathPages(PAGE_PATH.ALL_GAMES).replace(
            ':provider',
            'evoplay',
          )}
        />
      </div>
      {!isLoading ? (
        games?.length ? (
          <div className={styles.content}>{gamesJSX}</div>
        ) : (
          <NoData />
        )
      ) : (
        <Loader />
      )}
    </Section>
  );
};

export default memo(GamesByGeo);
